import Layout from 'components/layout/Layout'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import type { PricelistsProps } from 'features/pricelists/types'
import Pricelists from 'features/pricelists/Pricelists'
import { DEFAULT_NAMESPACES } from 'constants/i18n'
import { getUsableProducts } from 'utils/products'
import { Language } from 'types/language'
import { useTranslation } from 'next-i18next/pages'
import type { SeoProps } from 'components/seo/types'
import { Granularity } from 'types/chart'
import { cmsLib, metricsLib, productsLib } from 'lib/index'
import { groupPricelistsByEnvironment } from 'features/pricelists/utils'
import { EL_PRODUCT_CODES, GAS_PRODUCT_CODES, REGIONS } from '@boltenergy-be/shared'
import { mapCmsEncyclopediaArticlesToArticles } from 'utils/cms'
import dayjs from 'dayjs'
import { DATE_FORMAT_ISO } from 'features/registration/constants'

const PricelistsPage = (props: PricelistsProps) => {
  // i18n
  const { t } = useTranslation('pricelists')

  // Constants
  const SEO: SeoProps = {
    title: t('title')
  }

  return (
    <Layout seo={SEO}>
      <Pricelists {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ locale }) => {
  //Constants
  const currentDate = dayjs()
  const minStartDate = currentDate.subtract(18, 'month').format(DATE_FORMAT_ISO)
  const endDate = currentDate.endOf('year').format(DATE_FORMAT_ISO)

  const [apiProductsResponse, pricelistPageContent, priceHistory] = await Promise.all([
    productsLib.getProducts(),
    cmsLib.getPriceListPage(locale === Language.FRENCH ? Language.FRENCH : Language.DUTCH),
    metricsLib.getPriceHistory(Granularity.MONTH, minStartDate, endDate, 'kWh')
  ])

  if (!apiProductsResponse || !pricelistPageContent) throw new Error('Could not fetch the pricelists page data')

  // Get all products for region Flanders because all products are available in Flanders
  const usableProducts = getUsableProducts({ region: REGIONS.flanders, withPlenty: true })

  const ELProducts = usableProducts.map((p) => {
    return {
      name: pricelistPageContent.products?.find((product) => product.apiProduct.slug === p)?.name || p,
      pricelists: apiProductsResponse.electricity[EL_PRODUCT_CODES[p]].pricelists,
      product: p
    }
  })

  const NGProducts = usableProducts.map((p) => ({
    name: pricelistPageContent.products?.find((product) => product.apiProduct.slug === p)?.name || p,
    pricelists: apiProductsResponse.gas[GAS_PRODUCT_CODES[p]].pricelists,
    product: p
  }))

  const productsToShow: PricelistsProps['products'] = {
    electricity: groupPricelistsByEnvironment(ELProducts),
    gas: groupPricelistsByEnvironment(NGProducts)
  }

  const articles = pricelistPageContent?.encyclopediaArticles?.length
    ? mapCmsEncyclopediaArticlesToArticles(pricelistPageContent?.encyclopediaArticles)
    : []

  return {
    props: {
      products: productsToShow,
      encyclopediaArticles: articles,
      priceHistory,
      currentDate: currentDate.format(DATE_FORMAT_ISO),
      minStartDate: minStartDate,
      endDate,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'pricelists'], i18nConfig))
    },
    revalidate: 3600 * 2 // re-generate page when a request comes in, every 2 hours
  }
}

export default PricelistsPage
